const YOTPO_APP_KEY = 'O5p8NjD8OkNJ0VGv7psk82A8E3BtLuQZc2ZafMji';

class YotpoReviews extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector('[data-reviews-grid]');
    this.template = this.querySelector('[data-card-template]');
    this.errorEl = this.querySelector('[data-error]');
    this.lineClamp = parseInt(this.dataset.lineClamp, 10) || 6;
    this.perPage = parseInt(this.dataset.perPage, 10) || 9;
    this.randomize = this.dataset.randomize === 'true';

    try {
      let reviews = await this.fetchReviews();
      reviews = reviews.filter((r) => r.score === 5);
      if (!reviews.length) {
        this.showError();
        return;
      }
      if (this.randomize) reviews = this.shuffle(reviews);
      this.render(reviews.slice(0, this.perPage));
    } catch {
      this.showError();
    }
  }

  async fetchReviews() {
    const poolSize = this.randomize ? Math.max(40, this.perPage * 4) : Math.max(this.perPage * 2, 20);
    const params = new URLSearchParams({
      page: '1',
      perPage: String(poolSize),
      sort: this.dataset.sort || 'date,images,badge,rating',
    });
    const productId = this.dataset.productId;
    const scope = productId ? `product/${productId}/reviews` : 'reviews';
    const url = `https://api-cdn.yotpo.com/v3/storefront/store/${YOTPO_APP_KEY}/${scope}?${params}`;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Yotpo ${res.status}`);
    const data = await res.json();
    return data?.reviews || data?.response?.reviews || [];
  }

  shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  render(reviews) {
    const frag = document.createDocumentFragment();
    reviews.forEach((review) => frag.appendChild(this.buildCard(review)));

    this.grid.replaceChildren(frag);
    this.grid.removeAttribute('aria-busy');
  }

  buildCard(review) {
    const card = this.template.content.firstElementChild.cloneNode(true);

    const starsEl = card.querySelector('[data-stars]');
    const score = Math.round(review.score || 5);
    for (let i = 0; i < 5; i++) {
      const star = document.createElement('span');
      star.className = `size-4 block ${i < score ? 'text-amber-500' : 'text-neutral-200'}`;
      star.innerHTML = '<svg fill="currentColor" viewBox="0 0 14 13" class="size-full" aria-hidden="true"><path d="M7 0L9.163 4.384L14 5.09L10.5 8.498L11.326 13.316L7 11.04L2.674 13.316L3.5 8.498L0 5.09L4.837 4.384L7 0Z"/></svg>';
      starsEl.appendChild(star);
    }

    const content = card.querySelector('[data-content]');
    if (content) {
      content.textContent = review.content || '';
      content.style.webkitLineClamp = String(this.lineClamp);

      const toggle = card.querySelector('[data-toggle]');
      if (toggle) {
        requestAnimationFrame(() => {
          if (content.scrollHeight > content.clientHeight + 1) {
            toggle.classList.remove('hidden');
            toggle.addEventListener('click', () => {
              const wasExpanded = content.dataset.expanded === 'true';
              content.dataset.expanded = wasExpanded ? 'false' : 'true';
              content.style.webkitLineClamp = wasExpanded ? String(this.lineClamp) : '';
              toggle.textContent = wasExpanded ? 'More' : 'Less';
            });
          }
        });
      }
    }

    const dateEl = card.querySelector('[data-date]');
    if (dateEl) {
      const raw = review.created_at || review.date || review.submitted_at;
      const date = raw ? new Date(raw) : null;
      if (date && !isNaN(date)) {
        dateEl.textContent = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        dateEl.dateTime = date.toISOString();
        dateEl.classList.remove('hidden');
      }
    }

    const name = review.user?.display_name || review.name || 'Verified buyer';

    const imageSlot = card.querySelector('[data-image-slot]');
    if (imageSlot) {
      const image = this.extractImage(review);
      if (image) {
        const img = imageSlot.querySelector('[data-image]');
        img.src = image;
        img.alt = `Review by ${name}`;
        imageSlot.classList.remove('hidden');
      }
    }

    const nameEl = card.querySelector('[data-name]');
    if (nameEl) nameEl.textContent = name;

    const avatarSlot = card.querySelector('[data-avatar-slot]');
    if (avatarSlot) {
      const avatarUrl = review.user?.social_image || review.user?.image_url;
      if (avatarUrl) {
        const img = document.createElement('img');
        img.src = avatarUrl;
        img.alt = name;
        img.className = 'w-full h-full object-cover';
        img.loading = 'lazy';
        avatarSlot.appendChild(img);
      } else {
        avatarSlot.textContent = name.trim().charAt(0).toUpperCase();
      }
    }

    return card;
  }

  extractImage(review) {
    const first = review.images_data?.[0] || review.media?.[0];
    return first?.original_url || first?.thumb_url || first?.url || null;
  }

  showError() {
    this.grid?.remove();
    this.errorEl?.classList.remove('hidden');
  }
}

customElements.define('yotpo-reviews', YotpoReviews);
